/**
 * Log levels for the application
 */
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

/**
 * Centralized logging utility to standardize logging across the application
 */
export class Logger {
  private static readonly PROD_ENV = typeof import.meta !== 'undefined'
    ? import.meta.env?.PROD === true
    : false;
  private static readonly DEFAULT_NAMESPACE = 'app';

  /**
   * Production builds only emit warnings and errors by default.
   */
  private static shouldLog(level: LogLevel): boolean {
    if (!this.PROD_ENV) {
      return true;
    }

    return level === LogLevel.WARN || level === LogLevel.ERROR;
  }

  /**
   * Get the current timestamp in ISO format
   */
  private static getTimestamp(): string {
    return new Date().toISOString();
  }

  /**
   * Format a log message with timestamp and namespace
   */
  private static formatMessage(namespace: string, message: string): string {
    return `[${this.getTimestamp()}][${namespace}] ${message}`;
  }

  /**
   * Log a message at the DEBUG level (development only)
   * @param message Log message
   * @param namespace Optional namespace to categorize logs
   * @param data Optional data to include in the log
   */
  static debug(message: string, namespace = this.DEFAULT_NAMESPACE, ...data: unknown[]): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(this.formatMessage(namespace, message), ...data);
    }
  }

  /**
   * Log a message at the INFO level (development only)
   * @param message Log message
   * @param namespace Optional namespace to categorize logs
   * @param data Optional data to include in the log
   */
  static info(message: string, namespace = this.DEFAULT_NAMESPACE, ...data: unknown[]): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(this.formatMessage(namespace, message), ...data);
    }
  }

  /**
   * Log a message at the WARN level
   * @param message Log message
   * @param namespace Optional namespace to categorize logs
   * @param data Optional data to include in the log
   */
  static warn(message: string, namespace = this.DEFAULT_NAMESPACE, ...data: unknown[]): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(this.formatMessage(namespace, message), ...data);
    }
  }

  /**
   * Log a message at the ERROR level
   * @param message Log message
   * @param namespace Optional namespace to categorize logs
   * @param data Optional data to include in the log
   */
  static error(message: string, namespace = this.DEFAULT_NAMESPACE, ...data: unknown[]): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(this.formatMessage(namespace, message), ...data);
    }
  }

  /**
   * Log an exception (error object) with stack trace
   * @param error Error object
   * @param context Additional context about where the error occurred
   * @param namespace Optional namespace to categorize logs
   */
  static exception(error: unknown, context = 'Unknown context', namespace = this.DEFAULT_NAMESPACE): void {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const stack = error instanceof Error ? error.stack : 'No stack trace available';

    console.error(this.formatMessage(namespace, `Exception in ${context}: ${errorMessage}`));
    console.error(stack);
  }
}